/**
 * String-literal enums used across the API and frontend.
 *
 * Each is exported as both a const tuple and a TypeScript union type.
 * The tuples are useful for runtime iteration; the types for static checking.
 */

// ---------------------------------------------------------------------------
// User
// ---------------------------------------------------------------------------

export const UserRoles = ['ADMIN', 'ASSISTANT'] as const;
export type UserRole = (typeof UserRoles)[number];

// ---------------------------------------------------------------------------
// Project lifecycle
// ---------------------------------------------------------------------------

export const ProjectLifecycleStates = [
  'DFA_RECEIVED',
  'DFA_SUBMITTED',
  'CONTRACT_SIGNED',
  'PENDING_PARTS_APPROVAL',
  'ACTIVE',
  'AWAITING_PAYMENT',
  'CLOSED',
] as const;
export type ProjectLifecycleState = (typeof ProjectLifecycleStates)[number];

// ---------------------------------------------------------------------------
// Parts workflow (stored state — drives project lifecycle derivation)
// ---------------------------------------------------------------------------

export const PartsWorkflowStates = [
  'NOT_STARTED',
  'ENTERING',
  'PENDING_APPROVAL',
  'FINALISED',
] as const;
export type PartsWorkflowState = (typeof PartsWorkflowStates)[number];

// ---------------------------------------------------------------------------
// Project items (parts and sub-parts in one table)
// ---------------------------------------------------------------------------

export const ItemTypes = ['PART', 'SUB_PART'] as const;
export type ItemType = (typeof ItemTypes)[number];

// ---------------------------------------------------------------------------
// Project department roles
// ---------------------------------------------------------------------------

export const DepartmentRoles = ['PROCUREMENT', 'LOGISTICS', 'USER', 'QA', 'FINANCE'] as const;
export type DepartmentRole = (typeof DepartmentRoles)[number];

// ---------------------------------------------------------------------------
// Inspection location (default on project; per-event override on stage 9/11)
// ---------------------------------------------------------------------------

export const InspectionLocations = ['OUR_PREMISES', 'CLIENT_PREMISES', 'THIRD_PARTY_PREMISES', 'TBD'] as const;
export type InspectionLocation = (typeof InspectionLocations)[number];


// ---------------------------------------------------------------------------
// Contract commercial terms
// ---------------------------------------------------------------------------

export const TermsOfDelivery = [
  'FOR',
  'FOB',
  'CNF',
  'CFR',
  'CIF',
  'FCA',
  'EXW',
  'DAP',
  'DDP',
  'CPT',
  'CIP',
] as const;
export type TermsOfDeliveryValue = (typeof TermsOfDelivery)[number];

export const ProjectCurrencies = ['PKR', 'USD', 'OTHER'] as const;
export type ProjectCurrency = (typeof ProjectCurrencies)[number];

// ---------------------------------------------------------------------------
// Inspection outcome (stages 9, 11, 13)
// ---------------------------------------------------------------------------

export const InspectionOutcomes = ['ACCEPTED', 'OBSERVATION', 'REJECTED'] as const;
export type InspectionOutcome = (typeof InspectionOutcomes)[number];

// ---------------------------------------------------------------------------
// Order batch status
// ---------------------------------------------------------------------------

export const OrderBatchStatuses = ['ACTIVE', 'CANCELLED', 'REPLACED'] as const;
export type OrderBatchStatus = (typeof OrderBatchStatuses)[number];

// ---------------------------------------------------------------------------
// Bank guarantee status (derived from dates + replaces chain)
// ---------------------------------------------------------------------------

export const BankGuaranteeStatuses = [
  'ACTIVE', // submitted but not yet accepted
  'ACCEPTED', // accepted, in force
  'ENCASHED', // applied date set (client invoked the BG)
  'RELEASED', // released back to supplier
  'REPLACED', // a newer BG references this as its `replaces`
] as const;
export type BankGuaranteeStatus = (typeof BankGuaranteeStatuses)[number];

// ---------------------------------------------------------------------------
// Parts approval submission status & review action
// ---------------------------------------------------------------------------

export const SubmissionStatuses = [
  'PENDING',
  'APPROVED',
  'APPROVED_WITH_EDITS',
  'REJECTED',
  'SUPERSEDED', // reserved; not yet used in v1 flow
] as const;
export type SubmissionStatus = (typeof SubmissionStatuses)[number];

export const SubmissionReviewActions = [
  'APPROVE',
  'APPROVE_WITH_EDITS',
  'REJECT',
  'FINALIZE',
] as const;
export type SubmissionReviewAction = (typeof SubmissionReviewActions)[number];


// ---------------------------------------------------------------------------
// Payment events
// ---------------------------------------------------------------------------

export const PaymentEventTypes = [
  'INVOICE_SUBMITTED',
  'CHEQUE_RELEASED',
  'CHEQUE_CLEARED',
] as const;
export type PaymentEventType = (typeof PaymentEventTypes)[number];

// ---------------------------------------------------------------------------
// Draft saving
// ---------------------------------------------------------------------------

export const DraftTypes = [
  'PROJECT_CREATE',
  'GENERAL_PARTICULARS',
  'SCHEDULE_OF_STORES',
  'CSV_IMPORT',
] as const;
export type DraftType = (typeof DraftTypes)[number];

// ---------------------------------------------------------------------------
// Audit log actions
// ---------------------------------------------------------------------------

export const AuditActions = [
  // Generic CRUD
  'CREATE',
  'UPDATE',
  'DELETE', // soft delete
  'RESTORE', // un-delete (clear deletedAt)
  // Parts approval workflow
  'SUBMIT_PARTS',
  'APPROVE_PARTS',
  'APPROVE_PARTS_WITH_EDITS',
  'REJECT_PARTS',
  'FINALIZE_PARTS',
  // Batch lifecycle
  'CANCEL_BATCH',
  'REPLACE_BATCH',
  // BG instruments
  'CREATE_BG_INSTRUMENT',
  // Project closure
  'CLOSE_PROJECT',
  // Masters
  'ACTIVATE_MASTER',
  'DEACTIVATE_MASTER',
  'RENAME_MASTER',
] as const;
export type AuditAction = (typeof AuditActions)[number];

// ---------------------------------------------------------------------------
// Audit entity types
// ---------------------------------------------------------------------------

export const AuditEntityTypes = [
  'PROJECT',
  'PROJECT_ITEM',
  'ORDER_BATCH',
  'PART_STAGE_DATE',
  'INSPECTION_EVENT',
  'DELIVERY_EVENT',
  'PAYMENT_EVENT',
  'BANK_GUARANTEE',
  'PARTS_APPROVAL_SUBMISSION',
  'PROJECT_DEPARTMENT',
  'PROJECT_DEPARTMENT_CONTACT',
  'SUPPLIER',
  'SUPPLIER_CATEGORY',
  'CLIENT',
  'DEPARTMENT',
  'INCOTERM',
  'USER',
  // Patch E26 — attachments module
  'DOCUMENT',
  'COMMUNICATION',
] as const;
export type AuditEntityType = (typeof AuditEntityTypes)[number];

// ---------------------------------------------------------------------------
// Attachment module (E26)
// ---------------------------------------------------------------------------

export const AttachmentKinds = ['DOCUMENT', 'COMMUNICATION'] as const;
export type AttachmentKind = (typeof AttachmentKinds)[number];

export const DocumentTypes = [
  'DFA',
  'PO',
  'SCHEDULE_OF_STORES',
  'SUPPLIER_INVOICE',
  'CLIENT_INVOICE',
  'BG_INSTRUMENT',
  'INSPECTION_REPORT',
  'LETTER',
  'EMAIL_SCAN',
  'CHEQUE_COPY',
  'CERTIFICATE',
  'DRAWING',
  'AGREEMENT',
  'I_NOTE',
  'CRV',
  'OTHER',
] as const;
export type DocumentType = (typeof DocumentTypes)[number];

export const CommTypes = [
  'PHONE_CALL',
  'MEETING',
  'LETTER_RECEIVED',
  'LETTER_SENT',
  'EMAIL',
  'WHATSAPP',
  'SMS',
  'INTERNAL_NOTE',
] as const;
export type CommType = (typeof CommTypes)[number];

export const CommDirections = ['INBOUND', 'OUTBOUND', 'INTERNAL'] as const;
export type CommDirection = (typeof CommDirections)[number];

// ---------------------------------------------------------------------------
// Notification types
// ---------------------------------------------------------------------------

export const NotificationTypes = [
  'ENTITY_ENTERED_STAGE',
  'ITEM_OVERDUE',
  'INVOICE_UNLOCKED', // BG just got accepted on the project
  'INSPECTION_NON_ACCEPTED',
  'PARTS_SUBMISSION_PENDING', // for Admins
  'PARTS_SUBMISSION_REJECTED', // for Assistants on the project
  'WARRANTY_END_APPROACHING', // spec §10.4 — within 30-day window
  'BG_RELEASE_OVERDUE', // spec §10.4 — warranty end past, BG not released
  'BATCH_CANCELLED',
  'BATCH_REPLACED',
] as const;
export type NotificationType = (typeof NotificationTypes)[number];
